<template>
  <div style="margin: 0 20px 20px;">
    <div class="ad-header-row d-flex align-items-center justify-content-center flex-wrap">
      <div class="ad-header-left">
        <span style="font-size: 24px;">Delivery Failures</span>
      </div>
      <a href="#" class="white-link" v-on:click.prevent="shift_date(-1)">&lt;</a>
      &nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_fromDate" :config="options" :placeholder="details_fromDate"/>
      &nbsp;to&nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_toDate" :config="options" :placeholder="details_toDate"/>
      &nbsp;<a href="#" class="white-link" v-on:click.prevent="shift_date(1)">&gt;</a>
      &nbsp;&nbsp;<b-button v-on:click.prevent="load_data()">Search</b-button>
      <div class="ad-header-right">
        <b-button size="sm" variant="success" @click="showInfoModal = true" aria-label="more info" title="more info"><i class="fa fa-info-circle"></i></b-button>
      </div>
    </div>

    <b-modal v-model="showInfoModal" title="How Delivery Failures Works" size="lg" ok-only ok-title="Close">
      <div class="text-left" style="white-space: pre-line;">{{pageInfoText}}</div>
    </b-modal>

    <div v-if="loading" class="text-center" style="margin-top: 40px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <template v-if="!loading">
      <div v-if="!failedRecords.length" class="text-center" style="margin-top: 60px; color: #888;">
        <h4>No delivery failures found for this date range.</h4>
      </div>

      <template v-else>
        <!-- filter chips: click to toggle, counts are over the full unfiltered failure set -->
        <div class="df-filter-row">
          <span class="df-filter-label">Type:</span>
          <span
            v-for="et in errorTypeCounts"
            :key="'type-' + et.type"
            @click="toggleErrorType(et.type)"
            class="df-chip df-chip-clickable"
            :class="[typeChipClass(et.type), { 'df-chip-selected': selectedErrorTypes.includes(et.type) }]"
          >{{et.type}}: {{et.count}}</span>

          <span class="df-filter-label" style="margin-left: 10px;">Reason:</span>
          <span
            v-for="r in reasonCounts"
            :key="'reason-' + r.reason"
            @click="toggleReason(r.reason)"
            class="df-chip df-chip-clickable"
            :class="{ 'df-chip-selected': selectedReasons.includes(r.reason) }"
            :style="reasonChipStyle(r.reason)"
          >{{r.reason}}: {{r.count}}</span>
        </div>

        <b-table :items="filteredRecords" :fields="fields" bordered striped
                 :current-page="currentPage" :per-page="perPage">
          <template slot="errorTypeLabel" slot-scope="row">
            <span
              v-for="t in row.item.errorTypes"
              :key="t"
              class="df-mini-text"
              :style="typeTextStyle(t)"
            >{{t}}</span>
          </template>
          <template slot="reason" slot-scope="row">
            <span
              v-for="r in row.item.reasons"
              :key="r"
              class="df-mini-text"
              :style="reasonTextStyle(r)"
            >{{r}}</span>
          </template>
          <template slot="preview" slot-scope="row">
            <span class="df-preview">{{row.item.preview}}</span>
          </template>
        </b-table>
        <b-row>
          <b-col>
            <b-pagination
              v-model="currentPage"
              :total-rows="filteredRecords.length"
              :per-page="perPage"
              align="center"
            />
          </b-col>
        </b-row>
      </template>
    </template>
  </div>
</template>

<script>
import { get_alerts } from '../networking/apis'
import { mapState } from 'vuex'
import moment from 'moment'
import { justDate } from '@/helpers/dates'

// reason text is open-ended (varies by sms carrier), so colors are assigned by
// hashing the string into this fixed palette rather than a static per-value map.
// solid colors + white text, matching the tenant_manager chip style.
const REASON_COLOR_PALETTE = [
  '#0d6efd', '#20c997', '#6610f2', '#d63384',
  '#198754', '#0dcaf0', '#665191', '#b5511e',
]

export default {
  name: 'delivery_failures',
  data() {
    return {
      loading: true,
      items_full: [],
      selectedErrorTypes: [],
      selectedReasons: [],
      currentPage: 1,
      perPage: 25,
      showInfoModal: false,
      pageInfoText: `This page only shows alerts that failed to deliver - either an SMS that was rejected by the carrier, or an email that bounced or was marked as a complaint.

1. Pick a date range (defaults to the last month) and click Search.

2. Use the Type chips to filter by delivery channel: SMS Failed, Email Bounced, or Email Complaint. Selecting more than one Type shows failures matching any of them.

3. Use the Reason chips to filter by the specific cause - the raw carrier response for SMS (varies by phone carrier), or the decoded email status. Reasons that mention "opted out" are grouped together regardless of which phone number opted out. Selecting more than one Reason shows failures matching any of them.

4. Type and Reason filters combine together: if you select a Type AND a Reason, only failures matching both show up.

5. The table below lists every matching failure with its booking, vehicle, recipient, and the message that failed to send.`,
      details_fromDate: justDate(moment().subtract(1, 'month')),
      details_toDate: justDate(moment()),
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(360, 'days'),
        maxDate: moment(),
        keepOpen: false,
        showClear: true,
        showClose: true,
        toolbarPlacement: 'top',
      },
      fields: [
        { key: 'timeLabel', label: 'Time', sortable: true },
        { key: 'alert_type', label: 'Alert Type', sortable: true },
        { key: 'booking_id', label: 'Booking ID', sortable: true },
        { key: 'vehicle_id', label: 'Vehicle ID', sortable: true },
        { key: 'recipient', label: 'Recipient', sortable: true },
        { key: 'contact', label: 'Phone / Email', sortable: true },
        { key: 'errorTypeLabel', label: 'Error Type', sortable: true },
        { key: 'reason', label: 'Reason', sortable: false },
        { key: 'preview', label: 'Message Preview', sortable: false },
      ],
    }
  },
  async created() {
    this.load_data()
  },
  methods: {
    async load_data() {
      this.loading = true
      try {
        const { value: { data = [] } } = await get_alerts(
          this.user.tenant,
          '',
          moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
          moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        )
        if (data.summary && data.summary.timezone) moment.tz.setDefault(data.summary.timezone)
        this.items_full = data.alerts || []
      } catch (e) {
        this.$emit('showAlert', 'error retrieving data', 'danger', 5000)
      }
      this.currentPage = 1
      this.loading = false
    },
    shift_date(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
      this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
      this.load_data()
    },
    // mirrors the "Problems Only" definition on the Alert History page, so a
    // delivery counts as failed the same way everywhere.
    getLatestEmailStatus(item) {
      if (!item.email_msg_status || !item.email_msg_status.length) return null
      return item.email_msg_status.reduce((a, b) => (new Date(a.datetime) > new Date(b.datetime) ? a : b))
    },
    isAlertFailed(item) {
      const emailStatus = this.getLatestEmailStatus(item)
      const hasEmailIssue = emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase())
      const hasSmsIssue = item.provider_response && !item.provider_response.includes('Message has been accepted')
      return !!(hasEmailIssue || hasSmsIssue)
    },
    // the distinct, filterable error categories an item can match - an item can
    // match more than one (e.g. both its sms and email attempts failed)
    errorTypesFor(item) {
      const types = []
      if (item.provider_response && !item.provider_response.includes('Message has been accepted')) {
        types.push('SMS Failed')
      }
      const emailStatus = this.getLatestEmailStatus(item)
      if (emailStatus) {
        const status = emailStatus.status.toLowerCase()
        if (status === 'bounce') types.push('Email Bounced')
        if (status === 'complaint') types.push('Email Complaint')
      }
      return types
    },
    // per-type color, tenant_manager-style (solid color + white text), so each
    // error type reads at a glance
    typeChipClass(type) {
      const map = {
        'SMS Failed': 'df-type-sms-failed',
        'Email Bounced': 'df-type-email-bounced',
        'Email Complaint': 'df-type-email-complaint',
      }
      return map[type] || ''
    },
    // same per-type colors as the filter chip, but as bold text for the results table
    typeTextStyle(type) {
      const map = {
        'SMS Failed': '#fd7e14',
        'Email Bounced': '#dc3545',
        'Email Complaint': '#6f42c1',
      }
      return { color: map[type] || '#333', fontWeight: 'bold' }
    },
    // deterministic so a given reason string always gets the same color,
    // regardless of where it falls in the (count-sorted, so unstable) chip order
    hashString(str) {
      let hash = 0
      for (let i = 0; i < str.length; i++) {
        hash = (hash * 31 + str.charCodeAt(i)) | 0
      }
      return Math.abs(hash)
    },
    reasonChipStyle(reason) {
      const bg = REASON_COLOR_PALETTE[this.hashString(reason) % REASON_COLOR_PALETTE.length]
      return { backgroundColor: bg, borderColor: bg, color: '#fff' }
    },
    // same per-reason color as the filter chip, but as bold text for the results table
    reasonTextStyle(reason) {
      const color = REASON_COLOR_PALETTE[this.hashString(reason) % REASON_COLOR_PALETTE.length]
      return { color, fontWeight: 'bold' }
    },
    // distinct reason strings an item can match - raw sms provider response text
    // (varies by carrier) plus the decoded email status, so the reason chips are
    // more specific than the broad error-type chips above
    reasonsFor(item) {
      const reasons = []
      if (item.provider_response && !item.provider_response.includes('Message has been accepted')) {
        reasons.push(this.normalizeSmsReason(item.provider_response))
      }
      const emailStatus = this.getLatestEmailStatus(item)
      if (emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase())) {
        reasons.push(this.decodeEmailStatus(emailStatus.status))
      }
      return reasons
    },
    // "+12125864095 is opted out" varies per phone number, which would otherwise
    // fragment the opt-out cases into one chip per number - strip the number so
    // they all group under a single "opted out" chip
    normalizeSmsReason(raw) {
      const text = raw.trim()
      if (/opted out/i.test(text)) return 'opted out'
      return text
    },
    decodeEmailStatus(rawStatus) {
      const map = {
        send: 'sent',
        delivery: 'delivered',
        bounce: 'bounced',
        complaint: 'filed complaint',
        open: 'opened',
        click: 'clicked link',
      }
      return map[(rawStatus || '').toLowerCase()] || rawStatus
    },
    failureReason(item) {
      const parts = []
      if (item.provider_response && !item.provider_response.includes('Message has been accepted')) {
        parts.push(`sms: ${item.provider_response}`)
      }
      const emailStatus = this.getLatestEmailStatus(item)
      if (emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase())) {
        parts.push(`email: ${this.decodeEmailStatus(emailStatus.status)}`)
      }
      return parts.join(' | ') || 'unknown'
    },
    // the phone number and/or email address that actually bounced/failed
    failureContact(item) {
      const parts = []
      if (item.provider_response && !item.provider_response.includes('Message has been accepted')) {
        // driver/leader recipients live in sms_drivers/sms_leaders rather than
        // the plain sms field, so sms_all_recipients is the one that's actually
        // populated for the driver-facing alert types (LateAlert, MissingDVIR, ...)
        const phone = item.sms_all_recipients || item.sms || item.sms_drivers || item.sms_leaders
        if (phone) parts.push(phone)
      }
      const emailStatus = this.getLatestEmailStatus(item)
      if (emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase()) && item.email) {
        parts.push(item.email)
      }
      return parts.join(' | ')
    },
    firstLine(item) {
      const text = item.message || item.email_message || item.email_subject || ''
      const line = String(text).split('\n')[0].trim()
      return line.length > 80 ? line.slice(0, 80) + '…' : line
    },
    toggleErrorType(type) {
      const idx = this.selectedErrorTypes.indexOf(type)
      if (idx > -1) this.selectedErrorTypes.splice(idx, 1)
      else this.selectedErrorTypes.push(type)
      this.currentPage = 1
    },
    toggleReason(reason) {
      const idx = this.selectedReasons.indexOf(reason)
      if (idx > -1) this.selectedReasons.splice(idx, 1)
      else this.selectedReasons.push(reason)
      this.currentPage = 1
    },
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    failedRecords() {
      return this.items_full
        .filter(item => this.isAlertFailed(item))
        .map(item => {
          const errorTypes = this.errorTypesFor(item)
          return {
            key: item.movement_vehicle_id || `${item.alert_type}-${item.processed_time}`,
            processed_time: item.processed_time,
            timeLabel: moment(item.processed_time).format('MM/DD/YY h:mma'),
            alert_type: item.alert_type,
            booking_id: item.booking_id || '',
            vehicle_id: item.vehicle_id || item.movement_vehicle_id || '',
            recipient: item.sms_name || '',
            contact: this.failureContact(item),
            errorTypes,
            errorTypeLabel: errorTypes.join(', '),
            reasons: this.reasonsFor(item),
            reason: this.failureReason(item),
            preview: this.firstLine(item),
          }
        })
        .sort((a, b) => new Date(b.processed_time) - new Date(a.processed_time))
    },
    errorTypeCounts() {
      const counts = {}
      this.failedRecords.forEach(r => {
        r.errorTypes.forEach(t => { counts[t] = (counts[t] || 0) + 1 })
      })
      return Object.entries(counts)
        .map(([type, count]) => ({ type, count }))
        .sort((a, b) => b.count - a.count)
    },
    reasonCounts() {
      const counts = {}
      this.failedRecords.forEach(r => {
        r.reasons.forEach(reason => { counts[reason] = (counts[reason] || 0) + 1 })
      })
      return Object.entries(counts)
        .map(([reason, count]) => ({ reason, count }))
        .sort((a, b) => b.count - a.count)
    },
    filteredRecords() {
      let recs = this.failedRecords
      if (this.selectedErrorTypes.length) {
        recs = recs.filter(r => r.errorTypes.some(t => this.selectedErrorTypes.includes(t)))
      }
      if (this.selectedReasons.length) {
        recs = recs.filter(r => r.reasons.some(reason => this.selectedReasons.includes(reason)))
      }
      return recs
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.load_data()
      },
    },
  },
}
</script>

<style scoped>
.white-link {
  color: black;
  font-size: 28px;
  text-decoration: none;
}

.ad-header-row {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 25px;
  padding: 16px 20px;
  position: relative;
  background: linear-gradient(180deg, #f4f8fc 0%, #e8f0f8 100%);
  border: 1px solid #d3e2f0;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.ad-header-left {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
}

.ad-header-right {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
}

/* below this width the absolutely-positioned title/button would overlap the
   centered date-nav content as it wraps, so stack everything into its own
   full-width line instead */
@media (max-width: 768px) {
  .ad-header-left,
  .ad-header-right {
    position: static;
    transform: none;
    width: 100%;
    justify-content: center;
    margin: 6px 0;
  }
}

.df-preview {
  color: #555;
  font-style: italic;
  font-size: 13px;
}

.df-mini-text {
  display: inline-block;
  margin: 2px 6px 2px 0;
  font-size: 13px;
}

/* chip look matches tenant_manager.vue's .tenant-chip / .tenant-filter-clickable */
.df-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px 8px;
  margin: 10px 10px 16px;
  font-size: 16px;
}

.df-filter-label {
  font-weight: bold;
  margin-right: 4px;
  white-space: nowrap;
}

.df-chip {
  display: inline-block;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 3px 10px;
  white-space: nowrap;
  line-height: 1.3;
  font-weight: bold;
  background-color: #f8f9fa;
  color: #212529;
}

.df-chip-clickable {
  cursor: pointer;
}

.df-chip-clickable:hover {
  filter: brightness(0.95);
}

.df-chip-selected {
  border: 2px solid #dc3545 !important;
  box-shadow: 0 0 0 1px rgba(220, 53, 69, 0.2);
  padding: 2px 9px;
}

/* type chip colors, solid + white text, matching tenant_manager's semantic chips */
.df-chip.df-type-sms-failed { background-color: #fd7e14; border-color: #fd7e14; color: #fff; }
.df-chip.df-type-email-bounced { background-color: #dc3545; border-color: #dc3545; color: #fff; }
.df-chip.df-type-email-complaint { background-color: #6f42c1; border-color: #6f42c1; color: #fff; }
</style>
